import { useMemo } from 'react'
import { useStatuses } from './useWorkspaceStatuses'
import { useSortOrderMigration } from './useSortOrderMigration'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { useTasks } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'

interface UseFilteredTasksOptions {
  /** Additional filters passed to useTasks query */
  queryFilter?: Record<string, unknown>
  /** Filter predicate applied after fetching. Receives task and openStatusIds. */
  predicate: (task: TaskDocument, openStatusIds: ReadonlyArray<string>) => boolean
  /** Whether this hook needs status data (default: true) */
  needsStatuses?: boolean
  /** Restrict results to tasks whose primary assignee (userId) is the current user. */
  assignedToMeOnly?: boolean
}

/**
 * Shared hook that fetches tasks and applies a filter predicate.
 * Eliminates boilerplate across useInboxTasks, useTodayTasks, useUpcomingTasks, useSomedayTasks.
 */
export function useFilteredTasks({
  queryFilter,
  predicate,
  needsStatuses = true,
  assignedToMeOnly = false,
}: UseFilteredTasksOptions) {
  const currentUserId = useLoginStore((state) => state.userId)

  const {
    open: { statusIds: openStatusIds },
    isLoading: statusesLoading,
  } = useStatuses()

  const { tasks: allTasks, isLoading: tasksLoading } = useTasks({
    isDeleted: false,
    ...queryFilter,
  })

  // Lazy backfill sortOrder for pre-fix rows. No-op once every task has a
  // rank (post-backend-migration or after this hook has run once locally).
  useSortOrderMigration(allTasks)

  const tasks = useMemo(() => {
    if (!allTasks.length) return []
    if (needsStatuses && !openStatusIds.length) return []
    if (assignedToMeOnly && !currentUserId) return []

    return allTasks.filter((task) => {
      if (assignedToMeOnly && task.userId !== currentUserId) return false
      return predicate(task, openStatusIds)
    })
  }, [allTasks, openStatusIds, predicate, needsStatuses, assignedToMeOnly, currentUserId])

  return {
    tasks,
    isLoading: tasksLoading || (needsStatuses && statusesLoading),
  }
}
